import { normalizeProfileName } from '@garagegymbros/domain';
import type { SupabaseClient } from '@supabase/supabase-js';
import { demoMemberMedia } from '../state/demoMedia';
import { sampleMemberMediaFromRow } from '../state/sampleMedia';
import { bundledImage } from '../state/staticImages';

export type AuthFeaturedCover = {
  imageUrl: string;
  displayName: string;
  username: string;
};

const DEMO_FEATURED = (
  [
    ['demo-member-1', 'alexlifts', 'Alex Mercer'],
    ['demo-member-2', 'milesbuilt', 'Miles Carter'],
    ['demo-member-3', 'jordansgarage', 'Jordan Ellis'],
    ['demo-member-4', 'samliftsathome', 'Sam Rivera'],
    ['demo-member-5', 'devonbuilds', 'Devon Brooks'],
  ] as const
).flatMap(([id, username, displayName]) => {
  const cover = demoMemberMedia(id)?.cover;
  return cover ? [{ imageUrl: cover, displayName, username }] : [];
});

const SESSION_KEY = 'ggb.auth-featured-cover';

/** Browser-only demo gym covers used when the local preview is active. */
export function demoAuthFeaturedCovers(): AuthFeaturedCover[] {
  return DEMO_FEATURED;
}

/** Public featured gym covers from members with curated sample media. */
export async function loadAuthFeaturedCovers(client: SupabaseClient): Promise<AuthFeaturedCover[]> {
  const { data, error } = await client
    .from('profiles')
    .select('id, username, display_name, is_demo, sample_media')
    .not('sample_media', 'is', null)
    .limit(100);
  if (error || !data) return [];
  const covers: AuthFeaturedCover[] = [];
  for (const row of data) {
    const media = sampleMemberMediaFromRow(row);
    const username = typeof row.username === 'string' ? normalizeProfileName(row.username) : null;
    if (!media || !username || typeof row.display_name !== 'string' || !row.display_name.trim())
      continue;
    covers.push({
      imageUrl: media.gym_cover_url,
      displayName: row.display_name.trim(),
      username,
    });
  }
  return covers;
}

export function pickAuthFeaturedCover(
  covers: AuthFeaturedCover[],
  random: () => number = Math.random,
): AuthFeaturedCover | null {
  if (!covers.length) return null;
  return covers[Math.floor(random() * covers.length)] ?? null;
}

export function cssAuthFeaturedImage(imageUrl: string): string {
  if (/^\/images\/demo-members\/member-[1-5]-gym(?:-(?:640|1024))?\.(?:png|webp)$/.test(imageUrl))
    return `url('${bundledImage(imageUrl)!.src}')`;
  try {
    const url = new URL(imageUrl);
    if (url.protocol !== 'https:' || url.username || url.password) return '';
    return `url('${url.href.replace(/'/g, '%27')}')`;
  } catch {
    return '';
  }
}

function sessionStore(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

export function readCachedAuthFeaturedCover(
  store: Pick<Storage, 'getItem'> | null = sessionStore(),
): AuthFeaturedCover | null {
  try {
    const raw = store?.getItem(SESSION_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<AuthFeaturedCover>;
    if (
      typeof value.imageUrl !== 'string' ||
      typeof value.displayName !== 'string' ||
      typeof value.username !== 'string' ||
      !normalizeProfileName(value.username) ||
      !cssAuthFeaturedImage(value.imageUrl)
    )
      return null;
    return {
      imageUrl: value.imageUrl,
      displayName: value.displayName,
      username: value.username,
    };
  } catch {
    return null;
  }
}

export function writeCachedAuthFeaturedCover(
  cover: AuthFeaturedCover,
  store: Pick<Storage, 'setItem'> | null = sessionStore(),
) {
  try {
    store?.setItem(SESSION_KEY, JSON.stringify(cover));
  } catch {
    /* Session storage can be unavailable; the page still renders the pick. */
  }
}
